import { useCallback, useState } from 'react'
import { useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query'
import { api } from '../../api/client'
import { describeApiFailure } from '../../lib/failure'
import type { components } from '../../api/generated/schema'
import {
  galleryOwnerKey,
  galleryPath,
  type Gallery,
  type GalleryOwner,
  type LocationImageType,
} from './types'

type Schema = components['schemas']

export const galleryKeys = {
  all: ['galleries'] as const,
  gallery: (owner: GalleryOwner) => [...galleryKeys.all, ...galleryOwnerKey(owner)] as const,
}

/**
 * A gallery.
 *
 * `canManage` comes back on it, so no screen re-implements the access rules to decide
 * whether to draw an upload button — the same reasoning as `canAddCharacter` on a
 * campaign.
 */
export function useGallery(owner: GalleryOwner, enabled = true): UseQueryResult<Gallery> {
  return useQuery({
    queryKey: galleryKeys.gallery(owner),
    enabled,
    queryFn: () => api.get<Gallery>(galleryPath(owner)),
  })
}

/**
 * Every gallery mutation, bundled.
 *
 * **There is no envelope here and none is needed.** A gallery is a single-section
 * resource, so every write answers with the whole post-mutation gallery — which means the
 * patch rule the rest of the client runs (`applyEnvelope`, filtered per cache entry)
 * collapses to `setQueryData(key, response)`. The guarantee is the same one: the server
 * hands back full state and the client re-derives nothing.
 *
 * That also means no invalidation and no refetch, exactly as elsewhere.
 *
 * **Failures are surfaced, not swallowed.** The gallery endpoints answer `400` for real
 * domain rules with the reason in `detail` — "Cannot remove the main portrait", the
 * JPEG/PNG and 5 MB limits — and a write whose refusal vanishes is worse than one that
 * never had a button.
 */
export function useGalleryEditing(owner: GalleryOwner) {
  const queryClient = useQueryClient()
  const key = galleryKeys.gallery(owner)
  const path = galleryPath(owner)

  const [failure, setFailure] = useState<string | null>(null)

  const write = (gallery: Gallery) => {
    setFailure(null)
    queryClient.setQueryData<Gallery>(key, gallery)
  }

  const onError = (error: unknown) => {
    setFailure(describeApiFailure(error, 'These images are not yours to change.'))
  }

  const upload = useMutation({
    mutationFn: (input: { file: File; title?: string | undefined; type?: LocationImageType }) => {
      // multipart/form-data: the client sets no Content-Type for a FormData body, because
      // the browser has to add the boundary itself.
      const form = new FormData()
      form.append('file', input.file)
      if (input.title !== undefined && input.title !== '') form.append('title', input.title)
      if (input.type !== undefined) form.append('type', input.type)

      return api.post<Gallery>(path, form)
    },
    onSuccess: write,
    onError,
  })

  const update = useMutation({
    mutationFn: (input: { imageId: number; body: Schema['UpdateImageRequest'] }) =>
      api.put<Gallery>(`${path}/${input.imageId}`, input.body),
    onSuccess: write,
    onError,
  })

  const setMain = useMutation({
    mutationFn: (input: { imageId: number }) => api.post<Gallery>(`${path}/${input.imageId}/main`),
    onSuccess: write,
    onError,
  })

  const remove = useMutation({
    mutationFn: (input: { imageId: number }) => api.delete<Gallery>(`${path}/${input.imageId}`),
    onSuccess: write,
    onError,
  })

  return {
    upload,
    update,
    setMain,
    remove,
    failure,
    dismissFailure: useCallback(() => setFailure(null), []),
    isPending: upload.isPending || update.isPending || setMain.isPending || remove.isPending,
  }
}

export type GalleryEditing = ReturnType<typeof useGalleryEditing>
